import './style.css';
import {Map, View} from 'ol';
import TileLayer from 'ol/layer/Tile';
import WMTS, {optionsFromCapabilities} from 'ol/source/WMTS';
import WMTSCapabilities from 'ol/format/WMTSCapabilities';
import {get as getProjection} from 'ol/proj.js';
import proj4 from 'proj4';
import {register as registerProj} from 'ol/proj/proj4.js';
import {ScaleLine, defaults as defaultControls} from 'ol/control';

// --- British National Grid (EPSG:27700) --------------------------------------
// proj definition copied from http://epsg.io/27700.js
const PROJ_EPSG27700_DEF =
  '+proj=tmerc +lat_0=49 +lon_0=-2 +k=0.9996012717 +x_0=400000 +y_0=-100000 ' +
  '+ellps=airy +towgs84=446.448,-125.157,542.06,0.15,0.247,0.842,-20.489 +units=m +no_defs';
const PROJ_EPSG27700_EXTENT = [-238375.0, 0.0, 900000.0, 1376256.0];

proj4.defs('EPSG:27700', PROJ_EPSG27700_DEF);
proj4.defs('urn:ogc:def:crs:EPSG::27700', proj4.defs('EPSG:27700'));
registerProj(proj4);

const projection = getProjection('EPSG:27700');
projection.setExtent(PROJ_EPSG27700_EXTENT);

// --- WMTS layer, served from GeoServer's GeoWebCache ------------------------
// WMTS (not tiled WMS): the client takes GWC's own EPSG:27700 gridset straight
// from GetCapabilities, so every tile request lines up with a cacheable tile
// instead of forcing a live render. Pre-seed with:  ansible-playbook ... -t gwc_seed
const WMTS_CAPABILITIES_URL =
  'https://fehweb-osm.hydrosolutions.co.uk/geoserver/gwc/service/wmts?REQUEST=GetCapabilities&VERSION=1.0.0';
const WMTS_LAYER = 'osm:osm';
const WMTS_MATRIX_SET = 'EPSG:27700';
const WMTS_FORMAT = 'image/png';

async function buildMap() {
  const xml = await (await fetch(WMTS_CAPABILITIES_URL)).text();
  const capabilities = new WMTSCapabilities().read(xml);
  const wmtsOptions = optionsFromCapabilities(capabilities, {
    layer: WMTS_LAYER,
    matrixSet: WMTS_MATRIX_SET,
    format: WMTS_FORMAT,
    requestEncoding: 'KVP',
    projection: projection,
  });
  if (!wmtsOptions) {
    throw new Error(
      `WMTS layer "${WMTS_LAYER}" / matrix set "${WMTS_MATRIX_SET}" not advertised at ${WMTS_CAPABILITIES_URL}`,
    );
  }

  return new Map({
    target: 'map',
    layers: [
      new TileLayer({
        extent: PROJ_EPSG27700_EXTENT,
        source: new WMTS({
          ...wmtsOptions,
          // Countries have transparency, so do not fade tiles:
          transition: 0,
        }),
      }),
    ],
    controls: defaultControls({
      attributionOptions: {collapsed: false},
    }).extend([new ScaleLine()]),
    view: new View({
      projection: projection,
      // View zoom levels == GWC tile-matrix indices -> pixel-perfect tiles.
      resolutions: wmtsOptions.tileGrid.getResolutions(),
      center: [337297, 503995],
      zoom: 3,
    }),
  });
}

buildMap().catch((err) => {
  console.error('Failed to initialise the map:', err);
  const el = document.getElementById('map');
  if (el) {
    el.textContent = 'Map failed to load - see the browser console for details.';
  }
});
